import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import { EXPECTS_FORBIDDEN_META_KEY } from "~/configs/queryClient";
import { useTeam } from "~/contexts/TeamProvider";
import { StatusErrorLike } from "~/helpers/tryCatch";
import { useCableSubscription } from "~/hooks/useCableSubscription";

const FORBIDDEN_REFETCH_INTERVAL_MS = 30_000;

/**
 * Keeps the current team's billing state fresh mid-session, so a
 * subscription that gets blocked (a failed payment after the trial) sends the
 * user to billing without a reload: the backend broadcasts every subscription
 * status change, and a 403 from any request may mean the access changed
 * under the user. Window focus is covered by the team query itself.
 */
const SubscriptionSync = () => {
  const { teamId, refetchTeam } = useTeam();
  const queryClient = useQueryClient();

  // The channel streams the team that is current at subscribe time, so the
  // team id in the params makes a team switch resubscribe.
  useCableSubscription(
    "SubscriptionsChannel",
    teamId != null ? { team_id: teamId } : {},
    {
      received: (message) => {
        switch (message.action) {
          // The invoice a blocked team has to pay lands a few seconds after
          // the status change, with its own Stripe webhook.
          case "status-changed":
          case "invoice-opened":
            void refetchTeam();
            break;
          // A saved card lands through webhooks without a status change
          // (often twice per card): the billing page shows it, and it may
          // lift the block.
          case "payment-method-changed":
            void refetchTeam();
            void queryClient.invalidateQueries({
              queryKey: ["subscriptionPlans"],
            });
            break;
        }
      },
    },
  );

  useEffect(() => {
    let lastRefetchAt = 0;

    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== "updated" || event.action.type !== "error") return;
      if (event.query.meta?.[EXPECTS_FORBIDDEN_META_KEY]) return;
      if ((event.action.error as StatusErrorLike | null)?.status !== 403)
        return;

      // A page may fire several requests that all get refused.
      if (Date.now() - lastRefetchAt < FORBIDDEN_REFETCH_INTERVAL_MS) return;

      lastRefetchAt = Date.now();
      void refetchTeam();
    });
  }, [queryClient, refetchTeam]);

  return null;
};

export default SubscriptionSync;
